import { useEffect, useState, useRef } from "react";
import { Paper, Typography, Box, useTheme, Chip } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { tokens } from "../../../theme";
import { CallSheet, MemoryAssignment, SiteVisit } from "./types";
import { getCrewMemberTasks } from "./useCrewTask";

interface ScheduleWidgetProps {
  selectedDate: Date;
  crewMemberId: number;
}

type Tab = "schedule" | "notifications";

type TaskItem = {
  title: string;
  date: string | null;
  dateTime?: string | null;
  type: "call" | "visit" | "memory";
};

export default function ScheduleWidget({
  selectedDate,
  crewMemberId,
}: ScheduleWidgetProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [now, setNow] = useState(new Date());
  const [activeTab, setActiveTab] = useState<Tab>("schedule");
  const [tasks, setTasks] = useState<TaskItem[]>([]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const currentHourRef = useRef<HTMLDivElement>(null);

  const dateStr = selectedDate.toLocaleDateString("en-CA");

  useEffect(() => {
    const fetchTasks = async () => {
      try {
        const res = await getCrewMemberTasks(crewMemberId, dateStr);

        const mapped: TaskItem[] = [
          ...(res.callsheets || []).map((t: CallSheet) => ({
            title: t.stream_title,
            date: t.stream_date,
            dateTime: t.stream_start_time
              ? `${t.stream_date}T${formatTimeString(t.stream_start_time)}`
              : null,
            type: "call" as const,
          })),

          ...(res.site_visits || []).map((t: SiteVisit) => ({
            title: t.title,
            date: t.scheduled_date_time
              ? t.scheduled_date_time.split("T")[0]
              : null,
            dateTime: t.scheduled_date_time,
            type: "visit" as const,
          })),

          ...(res.memory_book_assignments || []).map((t: MemoryAssignment) => ({
            title: t.stream.name,
            date: t.due_date ? t.due_date.split("T")[0] : null,
            dateTime: t.due_date,
            type: "memory" as const,
          })),
        ];

        setTasks(mapped);
      } catch (err) {
        console.error("Failed to load tasks", err);
      }
    };

    if (crewMemberId) fetchTasks();
  }, [crewMemberId, dateStr]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (currentHourRef.current && scrollRef.current) {
      currentHourRef.current.scrollIntoView({
        block: "start",
        behavior: "smooth",
      });
    }
  }, [selectedDate]);

  const isToday = selectedDate.toDateString() === now.toDateString();

  const times = Array.from({ length: 13 }, (_, i) => i + 6);

  const formattedDate = selectedDate.toLocaleDateString("default", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });

  const formatTimeString = (time?: string) => {
    if (!time) return null;

    const clean = time.padStart(4, "0");

    return `${clean.slice(0, 2)}:${clean.slice(2)}`;
  };

  const todayTasks = tasks.filter((t) => t.date === dateStr);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        p: 3,
        flexGrow: 1,
        backgroundColor: colors.primary[900],
      }}
    >
      {/* HEADER */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box
            onClick={() => setActiveTab("schedule")}
            sx={{
              bgcolor:
                activeTab === "schedule" ? colors.primary[400] : "transparent",
              color:
                activeTab === "schedule"
                  ? colors.primary[900]
                  : colors.primary[400],
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <CalendarMonthIcon fontSize="small" />
          </Box>

          <Box
            onClick={() => setActiveTab("notifications")}
            sx={{
              bgcolor:
                activeTab === "notifications"
                  ? colors.primary[400]
                  : "transparent",
              color:
                activeTab === "notifications"
                  ? colors.primary[900]
                  : colors.primary[400],
              borderRadius: "50%",
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <NotificationsNoneOutlinedIcon fontSize="small" />
          </Box>
        </Box>

        <Box>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            color={colors.primary[700]}
          >
            {activeTab === "schedule" ? "My Calendar" : "My Notifications"}
          </Typography>

          <Typography variant="caption" color={colors.primary[300]}>
            {formattedDate}
          </Typography>
        </Box>
      </Box>

      {activeTab === "schedule" && (
        <Box
          ref={scrollRef}
          sx={{
            overflowY: "auto",
            maxHeight: 340,
            pr: 0.5,
          }}
        >
          {times.map((hour) => {
            const isCurrentHour = isToday && now.getHours() === hour;

            const time = `${hour.toString().padStart(2, "0")}:00`;

            const hourTasks = todayTasks.filter((task) => {
              if (!task.dateTime) return false;

              const timePart = task.dateTime.split("T")[1];

              if (!timePart) return false;

              const taskHour = parseInt(timePart.split(":")[0], 10);

              return taskHour === hour;
            });

            return (
              <Box
                key={hour}
                ref={isCurrentHour ? currentHourRef : null}
                sx={{ display: "flex", mb: 2, minHeight: 60 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    width: 45,
                    pt: 1,
                    fontWeight: isCurrentHour ? 700 : 500,
                    color: isCurrentHour
                      ? colors.primary[400]
                      : colors.primary[300],
                  }}
                >
                  {time}
                </Typography>

                <Box
                  sx={{
                    flexGrow: 1,
                    borderLeft: `2px dashed ${
                      isCurrentHour ? colors.primary[400] : colors.primary[800]
                    }`,
                    pl: 2,
                    pb: 2,
                  }}
                >
                  {/* TASKS */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {hourTasks.map((task, i) => (
                      <Chip
                        key={i}
                        label={task.title}
                        size="small"
                        color={task.type === "call" ? "primary" : "secondary"}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {activeTab === "notifications" && (
        <Box
          sx={{
            height: 340,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <NotificationsNoneOutlinedIcon
            sx={{ color: colors.primary[800], fontSize: 40 }}
          />
          <Typography color={colors.primary[300]}>
            Notifications will appear here
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
